<template>
	<div class="w-full">
		<div
			v-if="status === FTNIStatus.Loading"
			class="flex w-full items-center justify-center bg-gray-100 sm:h-[1900px] xl:h-[1050px]"
		>
			<LoadingSVG
				class="ml-2"
				variant="green"
			/>
		</div>
		<div
			v-else-if="status !== FTNIStatus.Ready"
			class="flex items-center justify-center"
		>
			<div class="flex flex-col items-center justify-center gap-4">
				<ExclamationTriangleIcon
					v-if="status !== FTNIStatus.Canceled"
					class="h-10 w-10 text-red-400"
				/>
				<strong>{{ errorInfo.title }}</strong>
				<p
					v-if="status !== FTNIStatus.Canceled"
					class="max-w-[300px] text-center text-slate-500"
				>
					{{ errorInfo.message }}
				</p>
				<button
					type="button"
					class="rounded-lg border border-green-500 px-16 py-2 text-green-500"
					@click="loadForm"
				>
					{{ t('document.tryAgain') }}
				</button>
			</div>
		</div>
		<iframe
			ref="FTNIForm"
			v-if="status === FTNIStatus.Ready && FTNIFormEnabled"
			class="w-full border-0 sm:h-[1900px] xl:h-[1050px]"
			:src="FTNIFormURL"
		></iframe>
		<hr class="my-12" />
	</div>
</template>

<script setup lang="ts">
import LoadingSVG from '@/components/common/LoadingSVG.vue'
import { FTNIStatus } from '@/enums/FTNIStatus'
import { PlanPaymentType } from '@/enums/PlanPaymentType'
import { usePresentationLocale } from '@/i18n'
import { axiosInstance } from '@/services/axiosInstance'
import { useAuthStore } from '@/stores/auth'
import { usePlanStore } from '@/stores/plan'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import { captureException } from '@sentry/vue'
import type { AxiosRequestConfig } from 'axios'
import { onMounted, ref, useTemplateRef } from 'vue'

const authStore = useAuthStore()
const sessionDocumentsStore = useSessionDocumentsStore()
const planStore = usePlanStore()
const FTNISessionID = ref<string>('')
const FTNIFormURL = ref<string>('')
const FTNIFormEnabled = ref<boolean>(false)
const FTNIForm = useTemplateRef<HTMLIFrameElement | null>('FTNIForm')
const authToken = ref<string | null>('')
const { t } = usePresentationLocale()

type FTNIError = {
	title: string
	message: string
}

const statusMap = new Map<number, FTNIStatus>([
	[-1, FTNIStatus.Canceled],
	[0, FTNIStatus.Fatal],
	[1, FTNIStatus.Ready],
	[2, FTNIStatus.Ready],
	[3, FTNIStatus.Declined],
	[4, FTNIStatus.Error],
])

const errorMap = new Map<
	Exclude<FTNIStatus, [FTNIStatus.Loading, FTNIStatus.Ready]>,
	FTNIError
>([
	[
		FTNIStatus.Fatal,
		{
			title: t('document.fatalTitle'),
			message: t('document.fatalMessage'),
		},
	],
	[
		FTNIStatus.Canceled,
		{
			title: t('document.FTNIErrors.canceledTitle'),
			message: t('document.FTNIErrors.canceledMessage'),
		},
	],
	[
		FTNIStatus.Declined,
		{
			title: t('document.FTNIErrors.declinedTitle'),
			message: t('document.FTNIErrors.declinedMessage'),
		},
	],
	[
		FTNIStatus.Error,
		{
			title: t('document.FTNIErrors.errorTitle'),
			message: t('document.FTNIErrors.errorMessage'),
		},
	],
])

const status = ref<FTNIStatus>(FTNIStatus.Ready)
const errorInfo = ref<FTNIError>({ title: '', message: '' })

onMounted(async () => {
	await loadForm()

	window.addEventListener('message', async (message) => {
		if (message.source !== FTNIForm.value?.contentWindow) {
			return
		}

		status.value = statusMap.get(message.data.status) ?? FTNIStatus.Fatal

		if (status.value === FTNIStatus.Ready && message.data.token) {
			await setTokenOnFTNISuccess(message.data.token)
		} else {
			setErrorState(message.data.message)
		}
	})
})

const loadForm = async () => {
	await getSessionID()
	await prepareForm()
}

const setTokenOnFTNISuccess = async (token: string) => {
	await planStore.updatePlan(sessionDocumentsStore.currentPlanHolderID, {
		payment: {
			paymentType: PlanPaymentType.CreditCard,
			paymentDetails: {
				token: token,
			},
		},
	})
	await sessionDocumentsStore.setPaymentStepTo('document')
}

const setErrorState = (message?: string) => {
	FTNIFormEnabled.value = false
	errorInfo.value = errorMap.get(status.value) ?? { title: '', message: '' }

	if (!errorInfo.value.message && message) {
		errorInfo.value.message = message
	}
}

const getSessionID = async () => {
	status.value = FTNIStatus.Loading
	authToken.value = await authStore.getAuthToken()
	const planHolder = planStore.getPlan(
		sessionDocumentsStore.currentPlanHolderID
	)

	const documentRequestConfig: AxiosRequestConfig = {
		method: 'GET',
		withCredentials: true,
		headers: {
			'X-PAPI-AUTHENTICATED-WITH': `${authToken.value}`,
		},
		params: {
			planId: sessionDocumentsStore.currentPlanHolderID,
			customerInfo: {
				FirstName: planHolder?.firstName,
				LastName: planHolder?.lastName,
				Address1: planHolder?.address,
				City: planHolder?.city,
				State: planHolder?.state,
				Zip: planHolder?.zip,
				Phone1: planHolder?.phone,
			},
		},
	}

	try {
		const documentResponse = await axiosInstance.get(
			import.meta.env.VITE_SESSION_ID_URL,
			documentRequestConfig
		)
		FTNISessionID.value = documentResponse.data.sessionId
	} catch (error) {
		captureException(error, {
			extra: { message: 'Error getting FTNI Session ID' },
		})
		status.value = FTNIStatus.Fatal
		setErrorState()
	}
}

const prepareForm = async () => {
	status.value = FTNIStatus.Loading

	await new Promise((resolve) => setTimeout(resolve, 300))

	if (!FTNISessionID.value) {
		status.value = FTNIStatus.Fatal
		setErrorState()
		return
	}

	// FTNIFormURL.value = 'https://localhost:5173/testiframe.html' // hardcoded iframe for testing FTNI status
	FTNIFormURL.value = `${import.meta.env.VITE_PURCHASE_API_URL}/v1/payment/ftni/form?sessionId=${FTNISessionID.value}&pUserToken=${authToken.value}`

	FTNIFormEnabled.value = true
	status.value = FTNIStatus.Ready
}
</script>
